import axios from 'axios';
import { ElMessage } from 'element-plus';
import router from '@/router';
import APP_CONFIG from './config';
import * as storage from './storage';

// ============ Token 管理 ============

const getToken = () => {
  return storage.get('token');
};

const clearTokenCache = () => {
  storage.clearAuth();
};

const redirectToLogin = () => {
  clearTokenCache();
  router.push('/login');
};

// ============ 错误提示 ============
const showError = (message) => {
  ElMessage.error({
    message: message || '请求失败，请稍后重试',
    duration: 3000
  });
};

// ============ Axios 实例 ============
const instance = axios.create({
  baseURL: APP_CONFIG.BASE_URL,
  timeout: 10000,
});

// ============ Token 刷新机制 ============
// 使用 Promise 缓存代替 isRefreshing + 队列，代码更简洁
let refreshPromise = null;

const refreshToken = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const currentRefreshToken = storage.get('refreshToken');
  if (!currentRefreshToken) {
    redirectToLogin();
    return Promise.reject(new Error('无 refreshToken'));
  }

  refreshPromise = instance.post('/base/user/refreshToken', {
    refreshToken: currentRefreshToken
  }, {
    _isRefreshRequest: true
  }).then(response => {
    const { token } = response.data;
    storage.set('token', token);
    return token;
  }).catch(error => {
    redirectToLogin();
    return Promise.reject(error);
  }).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

// 处理被踢下线等必须直接重新登录的场景（40103）
const handleForceReLogin = (message = '账号已在其他设备登录，请重新登录') => {
  redirectToLogin();
  showError(message);
  return Promise.reject({ code: 40103, message });
};

// 处理 401 的公共方法（消除两处重复逻辑）
const handle401 = (config) => {
  // refreshToken 请求本身返回 401，直接跳转登录页
  if (config && config._isRefreshRequest) {
    redirectToLogin();
    return Promise.reject({ code: 401, message: '登录已过期，请重新登录' });
  }

  // 正在刷新 token，等待刷新完成后自动重试
  if (refreshPromise) {
    return refreshPromise.then(newToken => {
      config.headers['Authorization'] = `Bearer ${newToken}`;
      return instance(config);
    }).catch(() => {
      return Promise.reject({ code: 401, message: '登录已过期，请重新登录' });
    });
  }

  // 未在刷新，触发一次刷新并重试原请求
  return refreshToken().then(newToken => {
    config.headers['Authorization'] = `Bearer ${newToken}`;
    return instance(config);
  }).catch(() => {
    return Promise.reject({ code: 401, message: '登录已过期，请重新登录' });
  });
};

// ============ 调试日志 ============

const logRequest = (config) => {
  console.groupCollapsed(`[请求] ${config.method?.toUpperCase()} ${config.baseURL || ''}${config.url}`);
  if (config.params && Object.keys(config.params).length) {
    console.log('Query 参数:', config.params);
  }
  if (config.data !== undefined && config.data !== null) {
    let body = config.data;
    if (typeof body === 'string') {
      try { body = JSON.parse(body); } catch (e) { console.log(e) }
    }
    console.log('Body 参数:', body);
  }
  if (config.headers) {
    console.log('Headers:', config.headers);
  }
  console.groupEnd();
};

const logResponse = (response) => {
  const { config } = response;
  console.log(
    `[响应] ${config.method?.toUpperCase()} ${config.url}`,
    '\n结果:', response.data
  );
};

// ============ 请求拦截器 ============
instance.interceptors.request.use(
  config => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    if (config.headers['Content-Type'] === 'multipart/form-data') {
      config.timeout = 60000;
    }

    logRequest(config);
    return config;
  },
  error => {
    showError('请求配置错误');
    return Promise.reject(error);
  }
);

// ============ 响应拦截器 ============
instance.interceptors.response.use(
  response => {
    logResponse(response);
    
    if (response.config._fullResponse) {
      if (response.data instanceof Blob) {
        const contentType = response.headers['content-type'] || '';
        if (contentType.includes('application/json')) {
          return response.data.text().then(text => {
            try {
              const result = JSON.parse(text);
              if (result.code === 401) {
                return handle401(response.config);
              }
              if (result.code === 40103) {
                return handleForceReLogin(result.message || result.msg);
              }
            } catch (e) { /* blob 非 JSON，忽略解析错误 */ }
            return response;
          });
        }
        return response;
      }
      if (response.data && response.data.code === 401) {
        return handle401(response.config);
      }
      if (response.data && response.data.code === 40103) {
        return handleForceReLogin(response.data.message || response.data.msg);
      }
      return response;
    }

    const data = response.data;

    if (data.code !== undefined && data.code !== 0) {
      switch (data.code) {
        case 401:
          return handle401(response.config);
        case 40103:
          return handleForceReLogin(data.message || data.msg);
        case 403:
          showError('没有操作权限');
          break;
        default:
          showError(data.message || data.msg || '操作失败');
          break;
      }
      return Promise.reject(data);
    }

    return data;
  },
  error => {
    if (!error.response) {
      showError('网络连接失败，请检查网络设置');
      return Promise.reject(error);
    }

    const { status, data, config } = error.response;

    if (config._fullResponse) {
      if (status === 401) {
        return handle401(config);
      }
      return Promise.reject(error);
    }

    switch (status) {
      case 401:
        return handle401(config);
      case 403:
        showError('没有操作权限');
        break;
      case 404:
        showError('请求的资源不存在');
        break;
      case 500:
        showError('服务器内部错误');
        break;
      default:
        showError((data && data.message) || `请求失败(${status})`);
        break;
    }

    return Promise.reject(error);
  }
);

export default instance;